import { useEffect, useRef, useState } from "react";

const WIDTH = 640;
const HEIGHT = 220;

const toPoint = (event, canvas) => {
  const rect = canvas.getBoundingClientRect();
  const source = event.touches?.[0] || event.changedTouches?.[0] || event;
  return {
    x: ((source.clientX - rect.left) / rect.width) * canvas.width,
    y: ((source.clientY - rect.top) / rect.height) * canvas.height,
  };
};

export default function SignaturePad({ value = "", existingImageUrl = "", disabled = false, onChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [showExisting, setShowExisting] = useState(Boolean(existingImageUrl) && !value);

  useEffect(() => {
    setShowExisting(Boolean(existingImageUrl) && !value);
  }, [existingImageUrl, value]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = "#0f172a";
    context.lineWidth = 2;
    context.lineCap = "round";
    context.lineJoin = "round";

    if (!value) return;
    const image = new Image();
    image.onload = () => {
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
    };
    image.src = value;
  }, [value]);

  const start = (event) => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    drawingRef.current = true;
    setShowExisting(false);
    context.beginPath();
    const point = toPoint(event, canvas);
    context.moveTo(point.x, point.y);
    event.preventDefault?.();
  };

  const move = (event) => {
    if (!drawingRef.current || disabled) return;
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    const point = toPoint(event, canvas);
    context.lineTo(point.x, point.y);
    context.stroke();
    event.preventDefault?.();
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange?.(canvas.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context || disabled) return;
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    setShowExisting(false);
    onChange?.("");
  };

  return (
    <div className="space-y-3">
      <div className="relative overflow-hidden rounded-xl border border-slate-300 bg-white">
        {showExisting ? <img src={existingImageUrl} alt="Saved signature" className="absolute inset-0 h-full w-full object-contain bg-white" /> : null}
        <canvas
          ref={canvasRef}
          width={WIDTH}
          height={HEIGHT}
          className={`h-52 w-full touch-none ${disabled ? "cursor-not-allowed opacity-70" : "cursor-crosshair"}`}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">Draw with mouse, touch, or stylus.</p>
        <button type="button" className="wc-btn-outline text-sm" onClick={clear} disabled={disabled}>Clear</button>
      </div>
    </div>
  );
}
